import { ref } from 'vue';
import {
	Stripe,
	StripeElementsOptions,
	StripeCardElementOptions,
	StripeCardElement,
	StripeError,
	loadStripe
} from '@stripe/stripe-js';
import {
	httpService,
	Nullable,
	waitForValue
} from '@/utils';
import { env } from '@/config';
import { usePaymentMethodsState } from './use-payment-methods.state';
import { loadPaymentMethods } from './billing.service';

const stripe = ref<Nullable<Stripe>>(null);
const initing = ref(false);
const pollTimeout = ref();

export function useStripe() {
	const state = usePaymentMethodsState();

	async function init() {
		if (initing.value || stripe.value) {
			return;
		}
		initing.value = true;
		stripe.value = await loadStripe(env.STRIPE_KEY!);
	}

	init();

	async function createCardElement(
		element: HTMLElement,
		options?: StripeElementsOptions,
		cardOptions?: StripeCardElementOptions
	) {
		await waitForValue(stripe);
		const el = stripe.value
			?.elements({
				appearance: {
					theme: 'flat'
				}
			})
			.create('card', {
				style: {
					base: {
						color: '#32325d',
						fontFamily: 'Avenir, sans-serif',
						fontSmoothing: 'antialiased',
						fontSize: '16px',
						'::placeholder': {
							color: '#32325d'
						}
					},
					invalid: {
						fontFamily: 'Avenir, sans-serif',
						color: '#fa755a',
						iconColor: '#fa755a'
					}
				}
			});
		el?.mount(element);
		return el;
	}

	async function createPaymentMethod(
		card: StripeCardElement
	): Promise<{ error: StripeError } | void> {
		const { clientSecret } = await httpService.post<{
			clientSecret: string;
		}>('company/billing/payment-intent');
		const { error, paymentIntent } =
			await stripe.value!.confirmCardPayment(
				clientSecret,
				{
					setup_future_usage: 'off_session',
					payment_method: {
						card: card
					}
				}
			);

		if (error) {
			return { error };
		}

		if (!paymentIntent.payment_method) {
			return;
		}

		await httpService.post(
			`company/billing/payment-methods/attach`,
			{
				paymentMethod:
					typeof paymentIntent.payment_method ===
					'string'
						? paymentIntent.payment_method
						: paymentIntent.payment_method.id,
				paymentIntentId: paymentIntent.id
			}
		);
	}

	return {
		createCardElement,
		createPaymentMethod
	};
}
